import { useAuth } from 'context/useAuth';
import { useEffect, useState } from 'react';
import { VehicleInterface } from 'types/VehicleInterface';
import { useCollection } from '../../functions/hooks/useCollection';
import { where } from 'firebase/firestore';
import { DriverInterface } from 'types/DriverInterface';
import { TamperType } from 'types/TamperType';
import { getDateFromTimestamp } from 'functions/deviceStatusUtils';

export function mapToVehicleInterface(vehicle: any, devices: any[], users: any[]): VehicleInterface {

  const device = devices.find(x => x.id === vehicle?.data?.deviceId && !x.id.startsWith('vs'))?.data || {};
  
  const drivers: DriverInterface[] = [users.find(x => x.id === vehicle?.data?.driverId)].filter(x => x).map((user: {id: string, data: any}) => ({
    vehicleId: vehicle?.id || "",
    firstName: user?.data?.profile?.firstName || '',
    lastName: user?.data?.profile?.lastName || '',
    email: user?.data?.profile?.email || '',
    idNumberIsSouthAfrican: !!user?.data?.profile?.idNumberIsSouthAfrican,
    idNumber: user?.data?.profile?.idNumber || '',
    phoneCode: user?.data?.profile?.phoneCode || '',
    phoneNumber: user?.data?.profile?.phoneNumber || '',
    id: user?.id || '',
    createdByUserId: '',
    createdOnDateTime: getDateFromTimestamp(user?.data?.createdAt)?.toISOString() || '',
    updatedByUserId: '',
    updatedOnDateTime: '',
  }));

  const lastDateTime = getDateFromTimestamp(device?.lastMessageTimestamp)?.toISOString()  || "";
  const gpsDateTime = getDateFromTimestamp(device?.LastLocation?.updatedAt)?.toISOString()  || "";

  return {
    id: vehicle?.id || "",
    accountHolderId: vehicle?.data?.userId || "",
    deviceActive: !!vehicle?.data?.deviceId,
    deviceId: vehicle?.data?.deviceId || "",
    deviceSerialNumber: vehicle?.data?.deviceId || "",
    engineNumber: vehicle?.data?.details?.engineNumber || "",
    auraDisabled: !!vehicle?.data?.integration?.auraDisabled,
    threeDtCode: vehicle?.data?.integration?.threeDtCode || "",
    
    make: vehicle?.data?.details?.make || "",
    makeVariant: vehicle?.data?.details?.make || "",
    model: vehicle?.data?.details?.model || "",
    nickname: vehicle?.data?.details?.nickname || "",
    registrationNumber: vehicle?.data?.details?.registrationNumber || "",
    registrationYear: vehicle?.data?.details?.registrationYear || "",
    
    userId: vehicle?.data?.userId || "",

    variant: vehicle?.data?.details?.make || "",
    vehicleColour: vehicle?.data?.details?.color || "",
    vehicleType: vehicle?.data?.details?.vehicleType || "",
    vinNumber: vehicle?.data?.details?.vinNumber || "",

    gpsLongitudeDegrees: device?.LastLocation?.latLng?.longitudeDeg || null,
    gpsLatitudeDegrees: device?.LastLocation?.latLng?.latitudeDeg || null,
    gpsAltitudeMeters: device?.LastLocation?.latLng?.altitudeMetre || null,
    gpsDateTime: gpsDateTime,
    lastDateTime: lastDateTime,
    
    isLocked: device?.StatusUpdateLog?.fuelLockEngaged || device?.StatusUpdateLog?.motorLockEngaged || "",
    isLockedDateTime: getDateFromTimestamp(device.LockResultLog?.logTimestamp)?.toISOString() || "",
    isTrackerOnly: device?.isTrackerOnly,
    
    createdOnDateTime: getDateFromTimestamp(vehicle?.data?.createdAt)?.toISOString() || "",

    auraCustomerId: 0,
    
    fitmentCenterActive: false,
    fitmentCenterArea: "",
    fitmentCenterCloseTime: "",
    fitmentCenterId: 0,
    fitmentCenterLatitude: 0.0,
    fitmentCenterLongitude: 0.0,
    fitmentCenterMaxInstallationsPerWeek: 0,
    fitmentCenterName: "",
    fitmentCenterOpenTime: "",
    fitmentCenterPhysicalAddress: "",
    
    installationAvailabilityFitmentCenterId: 0,
    installationAvailabilityId: 0,
    installationAvailabilityUtcDateEnd: "",
    installationAvailabilityUtcDateStart: "",
    installationAvailabilityUtcTimeEnd: "",
    installationAvailabilityUtcTimeStart: "",
    installationBookingActive: false,
    installationBookingFitmentCenterId: 0,
    installationBookingId: 0,
    installationBookingUtcDateTime: "",
    
    onBoardingStatus: "",
    onBoardingStatusId: 0,
    
    regionId: 0,
    regionName: "",
    
    subscriptionDepositAmountCents: 0,
    subscriptionDepositAmountPaid: false,
    subscriptionDeviceAmountCentsExcludingDepositAmountCents: 0,
    subscriptionDeviceAmountCentsIncludingDepositAmountCents: 0,
    subscriptionDeviceAmountPaid: false,
    subscriptionId: 0,
    subscriptionPlanDisplayName: "",
    subscriptionPlanId: 0,
    userEmail: "",
    userFirstName: "",
    userHasPassCode: false,
    userIdNumber: "",
    userLastName: "",
    userPhoneCode: "",
    userPhoneNumber: "",
    
    drivers: drivers,

    tamperActive: device?.TamperLog?.active === true && device?.TamperLog?.cancelled !== true && device?.TamperLog?.tamperType === TamperType.BATTERY,
    tamperGpsJammed: false,
    tamperCarBatteryConnected: false,
    tamperDateTime: "",

    isMoving: false,
    isMovingDateTime: "",
    
    temperatureDegreesCelsius: 0.0,
    temperatureDateTime: "",
  };
}

export default function useGetVehicles(filters?: string, filterData: any = {}) {
  const { authorisedUser } = useAuth();
  const vehiclesCollection = useCollection<any>('vehicles');
  const devicesCollection = useCollection<any>('devices');
  const usersCollection = useCollection<any>('users');
  const [vehicles, setVehicles] = useState<VehicleInterface[]>([]);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function getVehicles() {
      
      setLoadingVehicles(true);

      try {
        
        let users: any[] = [];
        let devices: any[] = [];
        let vehicles: VehicleInterface[] = [];
        
        switch (filters) {
          
          case 'VEHICLES_BY_CLIENT_ID':
            //`userId eq ${clientId}`
            //orderBy("createdAt", "desc")
            // Scope joins to the IDs actually referenced by this client's
            // vehicles — previously this loaded the entire users + devices
            // collections client-side which made View Account slow.
            const clientVehicleDocs = await vehiclesCollection.queryMany([where("userId", "==", filterData.clientId)]);
            if (cancelled) return;
            const driverIds = Array.from(new Set(clientVehicleDocs.map((v: any) => v.data?.driverId).filter(Boolean)));
            const deviceIds = Array.from(new Set(clientVehicleDocs.map((v: any) => v.data?.deviceId).filter(Boolean)));
            // Firestore 'in' supports up to 30 values per query — batch.
            for (let i = 0; i < driverIds.length; i += 30) {
              const chunk = driverIds.slice(i, i + 30);
              if (chunk.length === 0) continue;
              const part = await usersCollection.queryMany([where('__name__', 'in', chunk)]);
              if (cancelled) return;
              users.push(...part);
            }
            for (let i = 0; i < deviceIds.length; i += 30) {
              const chunk = deviceIds.slice(i, i + 30);
              if (chunk.length === 0) continue;
              const part = await devicesCollection.queryMany([where('__name__', 'in', chunk)]);
              if (cancelled) return;
              devices.push(...part);
            }
            vehicles = clientVehicleDocs.map(x => mapToVehicleInterface(x, devices, users));
            break;

          case 'VEHICLES_WITH_BOOKING_PENDING':
            //"onBoardingStatus eq 'Booking-Pending'"
            vehicles = []; // no need to support this
            break;

          case 'VEHICLES_WITH_SCHEDULED_BOOKING':
            //"onBoardingStatus eq 'Scheduled'"
            vehicles = []; // no need to support this
            break;

          default:
            const message = `Filter not handled : ${filters}`;
            console.error(message);
            alert(message);
        }

        if (cancelled) return;
        setVehicles(vehicles);

      } catch (error: any) {
        if (cancelled) return;
        //if (axios.isAxiosError(error)) {
          setError(error.message);
        //}
      } finally {
        if (!cancelled) {
          setLoadingVehicles(false);
          sortVehicles('');
        }
      }
    }
    getVehicles();
    return () => { cancelled = true; };
  }, [authorisedUser.accessToken, filters, devicesCollection, vehiclesCollection, filterData.clientId]);

  function sortVehicles(label: string) {
    switch (label) {
      case 'A - Z':
        setVehicles((prevState) => {
          return prevState.sort((a, b) =>
            a.userFirstName.localeCompare(b.userFirstName)
          );
        });
        break;
      case 'Z - A':
        setVehicles((prevState) => {
          return prevState.sort((a, b) =>
            b.userFirstName.localeCompare(a.userFirstName)
          );
        });
        break;
      default:
        setVehicles((prevState) => {
          return prevState.sort((a, b) =>
            b.createdOnDateTime.localeCompare(a.createdOnDateTime)
          );
        });
    }
  }

  return {
    vehicles,
    loadingVehicles,
    error,
    sortVehicles,
  };
}
